const DEFAULT_TIMEZONE = 'UTC'

const toValidTimezone = (value: unknown): string | null => {
  if (typeof value !== 'string') return null

  const candidate = value.trim().replace(/^"|"$/g, '')
  if (!candidate) return null

  try {
    Intl.DateTimeFormat(undefined, { timeZone: candidate })
    return candidate
  } catch {
    return null
  }
}

/**
 * Get user timezone from localStorage user object.
 */
export const getUserTimezone = (): string => {
  if (typeof window === 'undefined') return DEFAULT_TIMEZONE

  // 1) Prefer direct key (common in localStorage debugging/manual set).
  const directTimezone = toValidTimezone(window.localStorage.getItem('user_timezone'))
  if (directTimezone) return directTimezone

  try {
    const rawUser = window.localStorage.getItem('user')
    if (!rawUser) return DEFAULT_TIMEZONE

    // Handle either JSON payload or plain string payload.
    const parsedUser = JSON.parse(rawUser)
    const fromUser =
      toValidTimezone(parsedUser?.user_timezone) ||
      toValidTimezone(parsedUser?.user?.user_timezone)

    if (fromUser) return fromUser

    return DEFAULT_TIMEZONE
  } catch {
    // If `user` is not JSON, still try if it itself is a timezone string.
    const rawUser = window.localStorage.getItem('user')
    return toValidTimezone(rawUser) || DEFAULT_TIMEZONE
  }
}

/**
 * Format a date to locale-specific format with time
 * @param date - ISO string or Date object
 * @param locale - Browser locale (optional, uses user's locale by default)
 * @returns Formatted date and time string
 */
export const formatDateTimeLocale = (date: string | Date, locale?: string): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const userLocale = 'en-CA'
  const userTimezone = getUserTimezone()

  return dateObj.toLocaleString(userLocale, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: userTimezone,
  })
}

/**
 * Format a date as YYYY-MM-DD HH:mm in the user's timezone.
 * Missing or invalid dates display as a dash.
 */
export const formatDateTimeCanada24 = (date?: string | Date | null): string => {
  if (!date) return '-'

  const dateObj = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(dateObj.getTime())) return '-'

  const parts = new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
    timeZone: getUserTimezone(),
  }).formatToParts(dateObj)

  const part = (type: string) => parts.find(item => item.type === type)?.value || ''

  return `${part('year').padStart(4, '0')}-${part('month')}-${part('day')} ${part('hour')}:${part('minute')}`
}

/**
 * Calculate relative time difference (e.g., "3 days ago", "1 week ago")
 * @param date - ISO string or Date object
 * @returns Relative time string
 */
export const getRelativeTime = (date: string | Date): string => {
  const dateObj = typeof date === 'string' ? new Date(date) : date
  const now = new Date()
  const diffMs = now.getTime() - dateObj.getTime()
  const diffSecs = Math.floor(diffMs / 1000)
  const diffMins = Math.floor(diffSecs / 60)
  const diffHours = Math.floor(diffMins / 60)
  const diffDays = Math.floor(diffHours / 24)
  const diffWeeks = Math.floor(diffDays / 7)
  const diffMonths = Math.floor(diffDays / 30)
  const diffYears = Math.floor(diffDays / 365)

  if (diffYears > 0) return diffYears === 1 ? '1 year ago' : `${diffYears} years ago`
  if (diffMonths > 0) return diffMonths === 1 ? '1 month ago' : `${diffMonths} months ago`
  if (diffWeeks > 0) return diffWeeks === 1 ? '1 week ago' : `${diffWeeks} weeks ago`
  if (diffDays > 0) return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`
  if (diffHours > 0) return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`
  if (diffMins > 0) return diffMins === 1 ? '1 minute ago' : `${diffMins} minutes ago`

  return 'Just now'
}
